/**
 * Client Portal Layout
 * Simplified layout for client-facing portal — top nav bar, no sidebar
 */

import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useAuth } from '../../contexts/auth-context'
import { ClientPortalNav } from './client-portal-nav'
import { Moon, Sun, LogOut, Menu } from 'lucide-react'
import { useTheme } from '../../contexts/theme-context'
import { useClientDashboard } from '../../features/portal/hooks/use-client-dashboard'
import './client-portal-layout.css'

interface ClientPortalLayoutProps {
  children?: React.ReactNode
}

export function ClientPortalLayout({ children }: ClientPortalLayoutProps) {
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { data: dashboard } = useClientDashboard()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign out failed:', err)
    }
  }

  return (
    <div className="portal-layout">
      <header className="portal-header">
        <div className="portal-header-left">
          <div className="portal-logo">
            <img src="/logo.png" alt="SzB Certified Public Accountants" className="portal-logo-img" />
          </div>
          <button className="portal-hamburger-btn" onClick={() => setMobileNavOpen(o => !o)} aria-label="Toggle navigation">
            <Menu size={24} />
          </button>
        </div>

        <ClientPortalNav mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

        <div className="portal-header-right">
          <button className="portal-icon-btn" onClick={toggleTheme} title="Toggle theme">
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <div className="portal-user-info">
            {dashboard?.clientName && (
              <span className="portal-user-name">{dashboard.clientName}</span>
            )}
            <span className="portal-user-email">{user?.email}</span>
          </div>
          <button className="portal-icon-btn" onClick={handleSignOut} title="Sign out">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="portal-main">
        {children ?? <Outlet />}
      </main>
    </div>
  )
}
